import { Box, Paper, styled } from '@mui/material';
import { useEffect, type FC, type HTMLAttributes, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';

import { errorStrings } from '../../../constants/strings';
import { UI_BUTTON_PRIMARY } from '../../../constants/uiConstants';
import { errorStatusMessages } from '../../../router/reactErrorBoundary';
import { useZustand } from '../../../zustandStore';
import SimpleButton from '../../atoms/button/simpleButton';

import type { Theme } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }: { theme: Theme }) => ({
	width: '100%',
	height: '100%',
	backgroundColor: globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP
		? theme.palette.tsGoColor.pink
		: theme.palette.hsbcColor.white,
	color: globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? theme.palette.tsGoColor.white : theme.palette.hsbcColor.black,
}));

const StyledErrorBox = styled(Box)(({ theme }: { theme: Theme }) => ({
	left: '50%',
	transform: globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP
		? 'translateX(-50%) translateY(-50%)'
		: 'translateX(-70%) translateY(-70%)',
	top: '50%',
	position: 'absolute',
	margin: globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? 'inherit' : '0 80px 0 80px',
	padding: globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? 'inherit' : '27px 45px',
	border: globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? 'none' : '2px solid',
	borderColor: globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? 'inherit' : theme.palette.hsbcColor.light_gray,
}));

type ErrorPageProps = HTMLAttributes<HTMLElement> & { showPageNotFoundMessage?: boolean };

const ErrorPage: FC<PropsWithChildren<ErrorPageProps>> = ({ showPageNotFoundMessage = true }) => {
	const navigate = useNavigate();
	const { setHeaderName } = useZustand();
	const { heading, message } = useZustand(state => state.error);

	useEffect(() => {
		setHeaderName(globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? 'Tradeshift Go' : 'HSBC Business Virtual Cards');
	}, [setHeaderName]);

	return (
		<StyledPaper>
			<StyledErrorBox>
				<h1>{showPageNotFoundMessage ? errorStatusMessages[404] : heading}</h1>
				{globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? (
					<p>
						{showPageNotFoundMessage
							? 'You tried to access a page that does not exist. Please make sure you have the correct link.'
							: message}
					</p>
				) : (
					<p>{showPageNotFoundMessage ? errorStrings.HSBC_PAGE_NOT_FOUND_DESCRIPTION : message}</p>
				)}
				{globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? (
					<SimpleButton
						dataTestId="not-found-button"
						onClick={() => {
							void navigate('/wallet');
						}}
						color={UI_BUTTON_PRIMARY}
						style={{ margin: '10px', position: 'relative', left: '90%', transform: 'translateX(-70%)' }}
						label={errorStrings.HSBC_PAGE_NOT_FOUND_BUTTON}
					/>
				) : (
					<></>
				)}
			</StyledErrorBox>
		</StyledPaper>
	);
};

export default ErrorPage;
